// src/Graphql/customSubscriptions.ts
/*
import gql from 'graphql-tag';

const ON_NEW_VULN = gql`
  subscription OnNewVulnerability($webappid: ID!) {
    onNewVulnerability(webappid: $webappid) {
      id
      webappid
      testid
      reference
      dateFirstFound
      dateFixed
      dateLastSeen
      owner
      pageUrl
      vulnLocation
      details {
        label
        value
      }
      render
      riskRating
      videoURL
      vulnCategory
      vulnName
      vulnStatus
      stepsToReproduce
      recommendations
    }
  }
`;
*/

const ON_NEW_VULN = `
  subscription onNewVulnerability($webappid: ID!) {
    onNewVulnerability(webappid: $webappid) {
      id
      webappid
      testid
      reference
      dateFirstFound
      dateFixed
      dateLastSeen
      owner
      pageUrl
      vulnLocation
      details {
        label
        value
      }
      subFindings {
        title
        description
        riskLevel
        affectedRecord
        expected
        actual
        referenceLink
        referenceLinkNavigable
        stepsToReproduce
        recommendation
        evidence
      }
      render
      riskRating
      videoURL
      vulnCategory
      vulnName
      vulnStatus
      stepsToReproduce
      recommendations
    }
  }
`;

export interface SubscribeToVulnerabilitiesParams {
  subscriptionClient: any;
  webappid: string;
  onNext: (data: any) => void;
  onError?: (error: any) => void;
}

export async function subscribeToVulnerabilities({
  subscriptionClient,
  webappid,
  onNext,
  onError,
}: SubscribeToVulnerabilitiesParams): Promise<any> {
  if (!subscriptionClient) {
    return;
  }
  const { subscribe } = await subscriptionClient.subscribe({
    query: ON_NEW_VULN,
    variables: { webappid },
  });

  return subscribe({ next: onNext, error: onError });
}

// Slim live-discovery stream — onVulnerabilityDiscovered. Each vuln as it is
// found mid-run, WITHOUT the render screenshot blob / subFindings / httpExchanges
// (those exceed AppSync's 240KB per-message limit and aren't needed for a live
// "found so far" row). The full record + screenshot is fetched on demand once
// the test completes. Server-side gating is identical to onNewVulnerability
// (Subscription.onVulnerabilityDiscovered.js: tenant-scoped, admin-or-owner).
const ON_VULN_DISCOVERED = `
  subscription onVulnerabilityDiscovered($webappid: ID!) {
    onVulnerabilityDiscovered(webappid: $webappid) {
      id
      webappid
      testid
      reference
      riskRating
      vulnName
      vulnCategory
      vulnLocation
      pageUrl
      title
      dateFirstFound
    }
  }
`;

export async function subscribeToVulnerabilityDiscovered({
  subscriptionClient,
  webappid,
  onNext,
  onError,
}: SubscribeToVulnerabilitiesParams): Promise<any> {
  if (!subscriptionClient) {
    return;
  }
  const { subscribe } = await subscriptionClient.subscribe({
    query: ON_VULN_DISCOVERED,
    variables: { webappid },
  });

  return subscribe({ next: onNext, error: onError });
}

// ----------------------------------------------------------------------------
// Live update subscriptions
//
// Each of the four below mirrors the onNewVulnerability pattern: the backend
// publishes a record via a backend-only mutation, AppSync fans it out to
// every subscriber whose webappid argument matches. Frontend consumers
// receive the full record and merge it into local model state.
// ----------------------------------------------------------------------------

const ON_TEST_STATUS_CHANGE = `
  subscription onTestStatusChange($webappid: ID!) {
    onTestStatusChange(webappid: $webappid) {
      id
      webappid
      owner
      endTime
      endTrigger
      functionCount
      startTime
      startTrigger
      statistics {
        pagesVisited
        apisObserved
        blockedDeniedDomain
        blockedDeniedPath
        blockedSubdomain
        blockedLogout
        captchaPages
        authenticated
        authenticationReason
      }
      testStatus
      testStatusMessage
      retestExpiresAt
      isRetestOf
      originalTestDate
    }
  }
`;

const ON_WEBAPP_TEST_STATUS_CHANGE = `
  subscription onWebAppTestStatusChange($webappid: ID!) {
    onWebAppTestStatusChange(webappid: $webappid) {
      lastTestDate
      currentPhase
      allcrits
      allhighs
      allmeds
      alllows
      opencrits
      openhighs
      openmeds
      openlows
    }
  }
`;

const ON_VULN_STATUS_CHANGE = `
  subscription onVulnStatusChange($webappid: ID!) {
    onVulnStatusChange(webappid: $webappid) {
      id
      webappid
      testid
      reference
      dateFirstFound
      dateFixed
      dateLastSeen
      owner
      pageUrl
      vulnLocation
      details {
        label
        value
      }
      subFindings {
        title
        description
        riskLevel
        affectedRecord
        expected
        actual
        referenceLink
        referenceLinkNavigable
        stepsToReproduce
        recommendation
        evidence
      }
      riskRating
      vulnCategory
      vulnName
      vulnStatus
      stepsToReproduce
      recommendations
      requiresAuthn
    }
  }
`;

const ON_WEB_APPLICATION_CHANGE = `
  subscription onWebApplicationChange($webappid: ID!) {
    onWebApplicationChange(webappid: $webappid) {
      id
      name
      url
      render
      owner
      verificationString
      ownershipVerified
      allowedLinks
      deniedLinks
      apiDomains
      maxRequestsPerSecond
      featureTeamName
      frontEndFramework
      backEndFramework
      dataType
      userType
      dateToTest
      testFrequency
      dateAdded
      abbrev
      color
    }
  }
`;

export interface SubscribeByWebappIdParams {
  subscriptionClient: any;
  webappid: string;
  onNext: (data: any) => void;
  onError?: (error: any) => void;
}

async function subscribeWithQuery(
  query: string,
  { subscriptionClient, webappid, onNext, onError }: SubscribeByWebappIdParams
): Promise<any> {
  if (!subscriptionClient) {
    return;
  }
  const { subscribe } = await subscriptionClient.subscribe({
    query,
    variables: { webappid },
  });
  return subscribe({ next: onNext, error: onError });
}

export function subscribeToTestStatusChanges(params: SubscribeByWebappIdParams): Promise<any> {
  return subscribeWithQuery(ON_TEST_STATUS_CHANGE, params);
}

export function subscribeToWebAppTestStatusChanges(params: SubscribeByWebappIdParams): Promise<any> {
  return subscribeWithQuery(ON_WEBAPP_TEST_STATUS_CHANGE, params);
}

export function subscribeToVulnStatusChanges(params: SubscribeByWebappIdParams): Promise<any> {
  return subscribeWithQuery(ON_VULN_STATUS_CHANGE, params);
}

export function subscribeToWebApplicationChanges(params: SubscribeByWebappIdParams): Promise<any> {
  return subscribeWithQuery(ON_WEB_APPLICATION_CHANGE, params);
}
